import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, filter, switchMap, take } from 'rxjs';

import { AppState, ConfigService, NativeBridgeService } from '@fcom/core';
import { loginStatus, loginToken } from '@fcom/core/selectors/login.selector';
import { isPresent } from '@fcom/core/utils/utils';
import { LoginStatus } from '@fcom/core-api/login/interfaces/login.interface';

const containsUrl = (urlsToCheck: string[], url: string): boolean =>
  isPresent(urlsToCheck.find((urlToCheck: string) => url.indexOf(urlToCheck) >= 0));

@Injectable({
  providedIn: 'root',
})
export class LoginTokenInterceptor implements HttpInterceptor {
  private configService = inject(ConfigService);
  private nativeBridgeService = inject(NativeBridgeService);
  private store$ = inject<Store<AppState>>(Store);

  static readonly LOGIN_TOKEN_HEADER = 'Authorization';
  static readonly LOGIN_TOKEN_PREFIX = 'Bearer ';

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!containsUrl(this.configService.sendLoginTokenForUrls, req.url)) {
      return next.handle(req);
    }
    return combineLatest([
      this.store$.pipe(
        loginStatus(),
        filter((status: LoginStatus) => this.hasCorrectStatus(req, status))
      ),
      this.store$.pipe(loginToken()),
    ]).pipe(
      take(1),
      switchMap(([_status, token]) => {
        const request: HttpRequest<unknown> = isPresent(token)
          ? req.clone({
              headers: req.headers.set(
                LoginTokenInterceptor.LOGIN_TOKEN_HEADER,
                `${LoginTokenInterceptor.LOGIN_TOKEN_PREFIX}${btoa(token)}`
              ),
              withCredentials: true,
            })
          : req;
        return next.handle(request);
      })
    );
  }

  private hasCorrectStatus(req: HttpRequest<unknown>, status: LoginStatus): boolean {
    const notLoggedInsideApp = status === LoginStatus.INITIAL && this.nativeBridgeService.isInsideNativeWebView;
    const statusNotInitialOrPending = status !== LoginStatus.INITIAL && status !== LoginStatus.PENDING;
    const hasCorrectReqUrl = this.configService.cfg.loyaltyServices.legacy.apiProfileUrl.indexOf(req.url) >= 0;
    const statusPendingAndCustomerProfileUrl = status === LoginStatus.PENDING && hasCorrectReqUrl;

    return notLoggedInsideApp || statusNotInitialOrPending || statusPendingAndCustomerProfileUrl;
  }
}
